import React, { useEffect, useState } from 'react';
import { Row, Col, Divider, Button, Tooltip } from 'antd';
import {
  CustomButton,
  CustomLabel,
  CustomModal,
  CustomTable,
  CustomTabs,
  CustomTextArea,
  FormInput,
  FormSelect,
  UploadWithTable,
  useCustomToast,
  AssetSelector,
} from '../../../../components';
import {
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
  EyeOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import {
  UploadedFile,
} from '../../../../utils';
import { AuthResponse } from '../../../Auth/types';
import { ShareResponse, useGetAssetsList, useListTerceros } from '../../../../share';

import ModalAddItemActivo from '../components/ModalAddItemActivo';
import { TechnicalConceptAPI } from '../../api/technicalConceptAPI';
import { Asset } from '../components/ModalAddItemActivo';
import { DateTime } from 'luxon';
import { useAtom } from 'jotai';
import { dataForCreate } from '../../../../state/atoms';

interface TechnicalConceptCreateViewProps {
  formik: any;
  dependencies?: ShareResponse[];
  authData?: AuthResponse | null;
  onFiles: (files: UploadedFile[]) => void;
  files?: UploadedFile[];
  itemsAdded?: any;
  setItemsAdded?: any;
}

const TechnicalConceptCreateView: React.FC<TechnicalConceptCreateViewProps> = ({
  formik,
  authData,
  files,
  onFiles,
  dependencies,
  itemsAdded,
  setItemsAdded,
}) => {
  const { showToast } = useCustomToast();
  const [isModalAddItem, setIsModalAddItem] = useState<boolean>(false);
  const [locations, setLocation] = useState<{ id: number; name: string; city?: { name: string } }[]>([]);
  const [selectedItem, setSelectedItem] = useState<Asset | null>(null);
  const [modalAssets, setModalAssets] = useState<boolean>(false);
  const [itemSelected, setItemSelected] = useState<any>();
  const [formData] = useAtom(dataForCreate);
  const { mutateAsync: fetchAssets } = useGetAssetsList();

  const SIN_REGISTRO = 'Sin registro'

  const handleViewItem = (item: Asset) => {
    setSelectedItem(item);
    setIsModalAddItem(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locationData = await TechnicalConceptAPI.fetchParameters(
          ['locations'],
          { status_id: '1', branch_id: String(authData?.user?.branch_id) },
        );
        
        if (locationData) {
          setLocation(locationData.locations || []);
        }

        const plateCode = formData?.item?.plate_code;
        if (plateCode) {
          const { data } = await fetchAssets({ plate_code: plateCode });

          if (data && data.length > 0) {
            setItemsAdded((prevItems: any) => [
              ...prevItems,
              { ...data[0], uniqueKey: `${data[0].id}-${Date.now()}` },
            ]);
            formik.setFieldValue('items', [
              ...(formik.values.items || []),
              { itemable_id: data[0].id },
            ]);
          } else {
            showToast('error', 'Activo no encontrado');
          }
        }
      } catch (error) {
        console.error('Error fetching parameters:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (authData) {
      formik.setFieldValue('requester_id', authData.user.id);
    }
  }, [authData]);

  const columns: any = [
    { title: 'Item', dataIndex: 'id', key: 'id' },
    { title: 'Código/placa', dataIndex: 'plate_code', key: 'plate_code' },
    { title: 'Descripción', dataIndex: 'description', key: 'description' },
    {
      title: 'Clase de activo',
      dataIndex: 'asset_class',
      key: 'asset_class',
      render: (_: any, record: any) => record?.asset_class?.code + ' - ' + record?.asset_class?.name,
    },
    {
      title: 'Artículo',
      dataIndex: 'item',
      key: 'item',
      render: (_: any, record: any) => record?.item?.code + ' - ' + record?.item?.name,
    },
    {
      title: 'Adiciones',
      dataIndex: 'assets_count',
      key: 'assets_count',
    },
    {
      title: 'Estado del bien',
      dataIndex: ['good_status', 'status'],
      key: 'status',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: Asset) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Tooltip title="Ver activo">
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewItem(record)} />
          </Tooltip>

          <Tooltip title="Eliminar activo">
            <Button type="link" icon={<DeleteOutlined />} onClick={() => handleRemoveItem(record.id)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleAddItem = (newItem: Asset) => {
    const uniqueKey = `${newItem.id}-${Date.now()}`;

    setItemsAdded((prevItems: any) => [
      ...prevItems,
      { ...newItem, uniqueKey },
    ]);

    formik.setFieldValue('items', [
      ...(formik.values.items || []),
      { itemable_id: newItem.id },
    ]);
  };

  const handleModalAssetsSave = () => {
    if (itemSelected.length === 0) return;

    if ((formik.values.items || []).length >= 1) {
      showToast('error', 'Solo puedes añadir un activo');
      return;
    }

    const selected = itemSelected[0];

    setItemsAdded([selected]);
    formik.setFieldValue('items', [{
      ...selected,
      itemable_id: selected.id,
    }]);

    setItemSelected([]);
    setModalAssets(false);
  };

  const handleModalAssetsCancel = () => {
    setModalAssets(false);
  };

  const handleRemoveItem = (index: number) => {
    setItemsAdded(itemsAdded.filter((_: any, i: any) => i === index));
    formik.setFieldValue('items', []);
  };

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <FormInput
            label="Solicitante"
            name=""
            placeholder={`${authData?.user.document} - ${authData?.user.name} ${authData?.user.last_name}`}
            value=""
            disabled
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <FormInput
            label="Fecha de la solicitud"
            name="query_date"
            placeholder="Seleccione"
            type="text"
            disabled
            value={DateTime.now().toFormat('yyyy-MM-dd')}
            error={formik.errors.query_date}
            touched={formik.touched.query_date}
            onBlur={formik.handleBlur}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <FormInput
            label="Estado solicitud"
            name=""
            placeholder="Seleccione"
            type="text"
            disabled
            value="Nuevo"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <FormInput
            label="Tipo de bien"
            name="asset_type_id"
            placeholder="Seleccione"
            type="text"
            disabled
            value="Activo fijo"
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <FormSelect
            label="Dependencia"
            name="dependency_id"
            placeholder="Seleccione"
            value={formik.values.dependency_id}
            options={dependencies?.map((x) => ({
              name: x.name,
              id: x.id.toString(),
            }))}
            error={formik.errors.dependency_id}
            touched={formik.touched.dependency_id}
            onChange={(value) => formik.setFieldValue('dependency_id', value)}
            disabled
            onBlur={formik.handleBlur}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <FormSelect
            label="Ubicación"
            name="location_id"
            placeholder="Seleccione"
            value={formik.values.location_id}
            options={locations?.map((x) => ({
              name: x.name + ' / ' + x.city?.name,
              id: x.id.toString(),
            }))}
            error={formik.errors.location_id}
            touched={formik.touched.location_id}
            onChange={(value) => formik.setFieldValue('location_id', value)}
            onBlur={formik.handleBlur}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={12}>
          <CustomTextArea
            label="Observaciones"
            name="observations"
            placeholder="Observaciones"
            value={formik.values.observations}
            error={formik.errors.observations}
            touched={formik.touched.observations}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Col>
      </Row>

      <Divider />
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={12}>
          <CustomLabel text="Activo" level={4} />
        </Col>
        <Col
          xs={24}
          sm={12}
          md={8}
          lg={12}
          style={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
          }}
        >
          <CustomButton
            label="Añadir"
            icon={<PlusOutlined />}
            onClick={() => setModalAssets(true)}
          />
        </Col>
      </Row>

      <CustomTabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: (
              <Button icon={<UnorderedListOutlined />} type="link">
                Ítems
              </Button>
            ),
            children: (
              <>
                <CustomTable
                  dataSource={itemsAdded}
                  columns={columns}
                  rowKey="id"
                />
              </>
            ),
          },
          {
            key: '2',
            label: (
              <Button icon={<FileTextOutlined />} type="link">
                Documentos
              </Button>
            ),
            children: (
              <>
                <Row>
                  <Col xs={24}>
                    <UploadWithTable onFilesChange={onFiles} files={files} />
                  </Col>
                </Row>
              </>
            ),
          },
        ]}
      />
      <ModalAddItemActivo
        formik={formik}
        setIsModalAddItem={setIsModalAddItem}
        isModalAddItem={isModalAddItem}
        onAddItem={handleAddItem}
        addedAssets={itemsAdded}
        selectedItem={selectedItem}
      />

      {modalAssets && <CustomModal
        title="Selector de activos"
        labelSubmit="Añadir"
        open={modalAssets}
        width="95vw"
        onCancel={handleModalAssetsCancel}
        onSave={handleModalAssetsSave}
      >
        <AssetSelector
          open={modalAssets}
          goodsType="Activo fijo"
          tableTitle="Listado de activos"
          columnsFijos={[
            {
              title: '#',
              dataIndex: 'id',
              key: 'id',
            },
            {
              title: 'Código / placa',
              dataIndex: 'plate_code',
              key: 'plate_code',
            },
            {
              title: 'Descripción',
              dataIndex: 'description',
              key: 'description',
            },
            {
              title: 'Grupo de activo',
              dataIndex: 'asset_group',
              key: 'asset_group',
              render: (_: any, record: any) =>
                record.asset_group
                  ? `${record.asset_group.code} - ${record.asset_group.name}`
                  : SIN_REGISTRO,
            },
            {
              title: 'Clase de activo',
              dataIndex: 'asset_class',
              key: 'asset_class',
              render: (_: any, record: any) =>
                record.asset_class
                  ? `${record.asset_class.code} - ${record.asset_class.name}`
                  : SIN_REGISTRO,
            },
            {
              title: 'Articulo',
              dataIndex: 'item',
              key: 'item',
              render: (_: any, record: any) =>
                record.item
                  ? `${record.item.code} - ${record.item.name}`
                  : ' N/A',
            },
            {
              title: 'Responsable',
              dataIndex: 'responsible',
              key: 'responsible',
              render: (_: any, record: any) =>
                record.item
                  ? `${record.responsible.document} - ${record.responsible.name} ${record.responsible.last_name}`
                  : SIN_REGISTRO,
            },
            {
              title: 'Ubicación',
              dataIndex: 'location',
              key: 'location',
              render: (_: any, record: any) =>
                record.location
                  ? `${record.location.code} - ${record.location.name}`
                  : SIN_REGISTRO,
            },
            {
              title: '¿Es un vehículo?',
              dataIndex: 'is_vehicle',
              width: 120,
              key: 'is_vehicle',
               render: (_: any, record: any) => record.is_vehicle ? 'Sí' : 'No',
            }
          ]}
          onSelectedRowsChange={(selectedRowKeys: any) =>
            setItemSelected(selectedRowKeys)
          }
        />
      </CustomModal>}
    </>
  );
};

export default TechnicalConceptCreateView;
